/**
 * Offers and voucher codes.
 *
 * Sources: `isOfferValid` (app.js), `isOfferValidForDate` / `getActiveOffersForDate`
 * (doctor_dashboard.js) and the validation branch inside `applyDiscountCode`
 * (patient_dashboard.js).
 *
 * The legacy offer records were written by three different editors over time, so
 * a single offer can carry any of `validFrom`/`startDate`/`start` for its start
 * and `validTo`/`endDate`/`end` for its end, and any of
 * `discountPercent`/`discount`/`percent` for its value. `normalizeOffer` folds
 * all of those shapes into one, which is why the rest of the app never has to.
 */

/** Percentage carried by an offer, whichever field name it happens to use. */
export function offerPercent(offer) {
  if (!offer) return 0;
  const value = offer.discountPercent ?? offer.discount ?? offer.percent ?? 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Fold an offer's many historical field spellings into one predictable shape. */
export function normalizeOffer(offer) {
  if (!offer) return null;
  const start = offer.validFrom || offer.startDate || offer.start || null;
  // `validUntil` is the spelling the legacy extra-hours and booking offers used;
  // `expiry` is the only date the Offer table actually stores, so an offer that
  // came from the database has an end and no start.
  const end =
    offer.validTo || offer.validUntil || offer.endDate || offer.end || offer.expiry || start || null;
  return {
    id: offer.id ?? null,
    name: offer.name || offer.title || 'عرض',
    percent: offerPercent(offer),
    start,
    end,
    expiry: offer.expiry || end || null,
    active: offer.active !== false,
    raw: offer,
  };
}

/**
 * Legacy `isOfferValid`: an offer with no expiry never expires, and an expiry
 * date is inclusive of the day itself.
 */
export function isOfferValid(offer, now = new Date()) {
  const normalized = normalizeOffer(offer);
  if (!normalized || !normalized.percent) return false;
  if (!normalized.expiry) return true;

  const today = new Date(now).setHours(0, 0, 0, 0);
  const expiry = new Date(normalized.expiry).setHours(0, 0, 0, 0);
  if (Number.isNaN(expiry)) return true;
  return expiry >= today;
}

/**
 * An offer that is switched on but whose first day is still ahead. It is not
 * discounting today, so it must not be listed with today's running offers.
 * The start is read as a calendar day in local time.
 */
export function isOfferUpcoming(offer, now = new Date()) {
  const normalized = normalizeOffer(offer);
  if (!normalized || !normalized.percent || !normalized.active || !normalized.start) return false;
  const [y, m, d] = String(normalized.start).slice(0, 10).split('-').map(Number);
  const start = new Date(y, m - 1, d).getTime();
  if (Number.isNaN(start)) return false;
  return start > new Date(now).setHours(0, 0, 0, 0);
}

/**
 * True when a date key falls inside the offer's window (both ends inclusive).
 *
 * A missing start is unbounded: the Offer table stores only an `expiry`, so
 * every offer created through the app has an end and no start, and requiring
 * both would leave the calendar's offer pill permanently dark. A start with no
 * end stays a single-day offer, because `normalizeOffer` falls back to
 * `end = start` — that is how the legacy night offers were written. An offer
 * with neither is not a dated offer and matches no day.
 */
export function isOfferValidForDate(offer, dateKey) {
  const normalized = normalizeOffer(offer);
  if (!normalized || !normalized.percent || !dateKey) return false;
  if (normalized.active === false) return false;
  if (!normalized.start && !normalized.end) return false;
  if (normalized.start && normalized.start > dateKey) return false;
  if (normalized.end && normalized.end < dateKey) return false;
  return true;
}

/** Every offer that applies on a given day. */
export function getActiveOffersForDate(offers = [], dateKey) {
  return (offers || []).filter((offer) => isOfferValidForDate(offer, dateKey)).map(normalizeOffer);
}

/**
 * The day discount for a date: the single highest applicable offer.
 * Offers do not stack with one another — the legacy code took `Math.max`.
 */
export function bestDayDiscount(offers = [], dateKey) {
  return getActiveOffersForDate(offers, dateKey).reduce(
    (best, offer) => Math.max(best, offer.percent),
    0,
  );
}

/**
 * Night discount for a slot.
 *
 * Two independent sources, in the legacy precedence order:
 *   1. the doctor's standing night discount, for slots flagged as evening ones;
 *   2. a per-date extra-hours block carrying its own night offer, which wins
 *      when the slot being booked belongs to that block.
 */
export function nightDiscountFor({ doctor, dateKey, isNightSlot = false, isExtraHourSlot = false }) {
  let percent = 0;

  if (doctor?.hasNightDiscount && isNightSlot) {
    percent = Number(doctor.nightDiscountPercent) || 0;
  }

  if (isExtraHourSlot && Array.isArray(doctor?.extraHours)) {
    const block = doctor.extraHours.find((eh) => eh.date === dateKey);
    if (block?.hasNightOffer && block.nightOfferDiscount) {
      percent = Number(block.nightOfferDiscount) || 0;
    }
  }

  return percent;
}

/* ==========================================================================
   Voucher codes
   ========================================================================== */

/** Usage allowance per voucher type, from the legacy `applyDiscountCode`. */
export const VOUCHER_MAX_USES = { single: 1, double: 2, permanent: Infinity };

export const VOUCHER_ERRORS = {
  NOT_FOUND: 'NOT_FOUND',
  INACTIVE: 'INACTIVE',
  EXHAUSTED: 'EXHAUSTED',
  EXPIRED: 'EXPIRED',
};

/** Arabic messages, matching the strings the live UI showed. */
export const VOUCHER_MESSAGES = {
  [VOUCHER_ERRORS.NOT_FOUND]: 'كود الخصم غير صحيح.',
  [VOUCHER_ERRORS.INACTIVE]: 'هذا الكود غير مفعل حالياً.',
  [VOUCHER_ERRORS.EXHAUSTED]: 'تم تجاوز الحد الأقصى لاستخدام هذا الكود.',
  [VOUCHER_ERRORS.EXPIRED]: 'هذا الكود منتهي الصلاحية.',
};

const maxUsesFor = (voucher) =>
  VOUCHER_MAX_USES[voucher?.type] ?? VOUCHER_MAX_USES.single;

/**
 * Validate a voucher code against a doctor's voucher list.
 *
 * @returns {{valid: boolean, percent: number, reason: string|null, message: string, voucher: object|null}}
 */
export function validateVoucher(code, vouchers = [], now = new Date()) {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) {
    return { valid: false, percent: 0, reason: null, message: '', voucher: null };
  }

  const voucher = (vouchers || []).find(
    (v) => String(v.code || '').trim().toUpperCase() === normalized,
  );

  const fail = (reason) => ({
    valid: false,
    percent: 0,
    reason,
    message: VOUCHER_MESSAGES[reason],
    voucher: voucher || null,
  });

  if (!voucher) return fail(VOUCHER_ERRORS.NOT_FOUND);
  if (voucher.active === false) return fail(VOUCHER_ERRORS.INACTIVE);

  const maxUses = maxUsesFor(voucher);
  const usedCount = Number(voucher.usedCount) || 0;
  if (maxUses !== Infinity && usedCount >= maxUses) return fail(VOUCHER_ERRORS.EXHAUSTED);

  if (voucher.expiry) {
    const expiry = new Date(voucher.expiry).setHours(0, 0, 0, 0);
    const today = new Date(now).setHours(0, 0, 0, 0);
    if (!Number.isNaN(expiry) && expiry < today) return fail(VOUCHER_ERRORS.EXPIRED);
  }

  const percent = Number(voucher.discount ?? voucher.discountPercent ?? 0) || 0;
  return {
    valid: true,
    percent,
    reason: null,
    message: `تم تفعيل الخصم بنجاح (${percent}%)!`,
    voucher,
  };
}

/** Random voucher code, matching the legacy `generateVoucherCode` alphabet and length. */
export function generateVoucherCode(length = 8) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // ambiguous glyphs omitted
  let code = '';
  for (let i = 0; i < length; i += 1) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return code;
}

/** Remaining uses for a voucher — `Infinity` for permanent codes. */
export function voucherUsesRemaining(voucher) {
  const maxUses = maxUsesFor(voucher);
  if (maxUses === Infinity) return Infinity;
  return Math.max(0, maxUses - (Number(voucher?.usedCount) || 0));
}
